import React, { useState } from "react";

import s from "./login.module.scss";

import cn from "classnames";
import { QRCodeSVG } from "qrcode.react";
import {
  Button,
  Card,
  Col,
  Form,
  Input,
  Layout,
  Row,
  Space,
  Typography,
  message,
} from "antd";
import axAPI from "../../api/axios-instance";
import { clearMe } from "../../access/me";

const { Text, Title } = Typography;

export interface ILoginForm {
  username: string;
  password: string;
}

type AuthStep =
  | { step: "credentials" }
  | { step: "otp" }
  | { step: "setup"; otpauthUrl: string };

type AuthResponse = {
  status: "OK" | "TOTP_REQUIRED" | "TOTP_SETUP_REQUIRED";
  access_token?: string;
  refresh_token?: string;
  otpauth_url?: string;
};

export const Login: React.FC = () => {
  const [form] = Form.useForm<ILoginForm>();
  const [otpForm] = Form.useForm<{ otp: string }>();
  const [authStep, setAuthStep] = useState<AuthStep>({ step: "credentials" });
  const [credentials, setCredentials] = useState<ILoginForm>();
  const [loading, setLoading] = useState(false);

  const submit = async (values: ILoginForm, otp?: string) => {
    setLoading(true);
    try {
      const response = await axAPI.post<AuthResponse>("/authorization/token", {
        username: values.username,
        password: values.password,
        otp,
      });
      const data = response.data;

      if (data.status === "TOTP_SETUP_REQUIRED" && data.otpauth_url) {
        setCredentials(values);
        setAuthStep({ step: "setup", otpauthUrl: data.otpauth_url });
        return;
      }
      if (data.status === "TOTP_REQUIRED") {
        setCredentials(values);
        setAuthStep({ step: "otp" });
        return;
      }
      if (data.status === "OK" && data.access_token && data.refresh_token) {
        localStorage.setItem("auth", data.access_token);
        localStorage.setItem("refresh_token", data.refresh_token);
        clearMe();
        window.location.replace("/");
        return;
      }
      message.error("Не удалось войти. Повторите попытку");
    } catch (error: any) {
      if (error?.response?.status === 401) {
        message.error(
          otp !== undefined
            ? "Неверный код подтверждения"
            : "Неверный логин или пароль"
        );
      } else {
        message.error("Ошибка на сервере. Повторите попытку позже");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleOtpSubmit = async ({ otp }: { otp: string }) => {
    if (!credentials) {
      setAuthStep({ step: "credentials" });
      return;
    }
    await submit(credentials, otp);
  };

  const backToCredentials = () => {
    otpForm.resetFields();
    setAuthStep({ step: "credentials" });
  };

  const CardTitle = (
    <Title level={5} className={s.title}>
      {authStep.step === "credentials"
        ? "Авторизация"
        : authStep.step === "setup"
        ? "Настройка двухфакторной защиты"
        : "Код подтверждения"}
    </Title>
  );

  const otpInput = (
    <Form.Item
      name="otp"
      label="Код из приложения"
      rules={[
        { required: true, message: "Введите 6-значный код" },
        { pattern: /^\d{6}$/, message: "Код — 6 цифр" },
      ]}
    >
      <Input
        size="large"
        placeholder="000000"
        maxLength={6}
        autoComplete="one-time-code"
        autoFocus
      />
    </Form.Item>
  );

  return (
    <Layout className={s.layout}>
      <Row
        justify="center"
        align="middle"
        style={{
          height: "100vh",
        }}
      >
        <Col xs={22}>
          <div className={s.container}>
            <div className={s.imageContainer}>
              <Title className={cn(s.title, s.mainTitle)} level={3}>
                FNL CMS
              </Title>
            </div>
            <Card title={CardTitle} headStyle={{ borderBottom: 0 }}>
              {authStep.step === "credentials" && (
                <Form<ILoginForm>
                  layout="vertical"
                  form={form}
                  onFinish={(values) => submit(values)}
                  requiredMark={false}
                >
                  <Form.Item
                    name="username"
                    label="Логин"
                    rules={[{ required: true, message: "Укажите логин" }]}
                  >
                    <Input size="large" placeholder="Имя пользователя" />
                  </Form.Item>
                  <Form.Item
                    name="password"
                    label="Пароль"
                    rules={[{ required: true, message: "Укажите пароль" }]}
                    style={{ marginBottom: "12px" }}
                  >
                    <Input
                      type="password"
                      placeholder="●●●●●●●●"
                      size="large"
                    />
                  </Form.Item>
                  <Button
                    loading={loading}
                    type="primary"
                    size="large"
                    htmlType="submit"
                    block
                  >
                    Авторизоваться
                  </Button>
                </Form>
              )}

              {authStep.step === "setup" && (
                <Space direction="vertical" size={16} style={{ width: "100%" }}>
                  <Text>
                    Отсканируйте QR-код в приложении <b>Яндекс ID</b> (раздел
                    «Одноразовые коды») или другом приложении-аутентификаторе,
                    затем введите код из приложения.
                  </Text>
                  <div style={{ display: "flex", justifyContent: "center" }}>
                    <QRCodeSVG value={authStep.otpauthUrl} size={200} />
                  </div>
                  <Form
                    form={otpForm}
                    layout="vertical"
                    onFinish={handleOtpSubmit}
                  >
                    {otpInput}
                    <Button
                      loading={loading}
                      type="primary"
                      size="large"
                      htmlType="submit"
                      block
                    >
                      Подтвердить и войти
                    </Button>
                  </Form>
                  <Button type="link" block onClick={backToCredentials}>
                    Назад
                  </Button>
                </Space>
              )}

              {authStep.step === "otp" && (
                <Space direction="vertical" size={16} style={{ width: "100%" }}>
                  <Text>
                    Введите код из приложения-аутентификатора (Яндекс ID).
                  </Text>
                  <Form
                    form={otpForm}
                    layout="vertical"
                    onFinish={handleOtpSubmit}
                  >
                    {otpInput}
                    <Button
                      loading={loading}
                      type="primary"
                      size="large"
                      htmlType="submit"
                      block
                    >
                      Войти
                    </Button>
                  </Form>
                  <Button type="link" block onClick={backToCredentials}>
                    Назад
                  </Button>
                </Space>
              )}
            </Card>
          </div>
        </Col>
      </Row>
    </Layout>
  );
};
